import type { ChatAssistantMessage, ChatHistoryItem, ChatSlicesToolCallResult } from '../types/chat'

import { createToolCallResultLookup } from '../components/scenarios/chat/components/tool-call-results'
import { sanitizeConnectionError } from './chat-connection'

export type WorkbenchToolRisk = 'read-only' | 'low-risk-write' | 'side-effect'
export type WorkbenchConfirmation = 'none' | 'required' | 'disabled'
export type WorkbenchStatus = 'running' | 'pending-confirmation' | 'success' | 'failed' | 'timeout' | 'rejected' | 'info' | 'cancelled'

export interface WorkbenchEvent {
  id: string
  at?: number
  kind: 'user' | 'selection' | 'context' | 'tool' | 'reply' | 'error'
  status: WorkbenchStatus
  durationMs?: number
  summary: string
  toolName?: string
}

export interface WorkbenchToolFact {
  name: string
  risk: WorkbenchToolRisk
  confirmation: WorkbenchConfirmation
}

const summaryLimit = 280

/**
 * Classifies a registered tool for the read-only workbench.
 * Side-effect tools stay disabled in this view. The workbench does not run them.
 *
 * @example
 * classifyWorkbenchTool('save_local_note')
 * // => { name: 'save_local_note', risk: 'low-risk-write', confirmation: 'required' }
 */
export function classifyWorkbenchTool(name: string): WorkbenchToolFact {
  if (name === 'save_local_note' || name === 'save_reminder' || name === 'create_reminder' || name === 'complete_reminder')
    return { name, risk: 'low-risk-write', confirmation: 'required' }
  if (name === 'search_public' || name === 'read_held_context' || name === 'list_reminders' || name === 'web_search' || name.startsWith('weather__'))
    return { name, risk: 'read-only', confirmation: 'none' }
  if (/computer|click|mouse|keyboard|login|payment|\bpay\b/i.test(name))
    return { name, risk: 'side-effect', confirmation: 'disabled' }
  return { name, risk: 'read-only', confirmation: 'none' }
}

/**
 * Projects one chat session into a redacted timeline.
 * The current provider and model are one selection row. History does not store them per turn.
 *
 * @example
 * projectWorkbenchTimeline([{ role: 'user', content: 'save this', createdAt: 1, id: 'u1' }], { provider: 'deepseek', model: 'deepseek-flash' })
 * // => [{ id: 'selection', kind: 'selection', status: 'info', summary: 'deepseek / deepseek-flash' }, { id: 'u1', at: 1, kind: 'user', status: 'info', summary: 'save this' }]
 */
export function projectWorkbenchTimeline(
  messages: ChatHistoryItem[],
  selection: { provider: string, model: string },
  secrets: string[] = [],
): WorkbenchEvent[] {
  const events: WorkbenchEvent[] = [present({
    id: 'selection',
    kind: 'selection',
    status: 'info',
    summary: redact(`${selection.provider || 'none'} / ${selection.model || 'none'}`, secrets),
  })]
  let previousAt: number | undefined

  for (const message of messages) {
    if (message.role === 'system')
      continue
    const at = message.createdAt
    const durationMs = typeof at === 'number' && typeof previousAt === 'number' && at >= previousAt
      ? at - previousAt
      : undefined
    if (typeof at === 'number')
      previousAt = at

    if (message.role === 'error') {
      events.push(present({
        id: message.id ?? `error-${events.length}`,
        at,
        kind: 'error',
        status: 'failed',
        durationMs,
        summary: clip(redact(textFrom(message.content), secrets)),
      }))
      continue
    }

    if (message.role === 'user') {
      events.push(present({
        id: message.id ?? `user-${events.length}`,
        at,
        kind: 'user',
        status: 'info',
        durationMs,
        summary: clip(redact(textFrom(message.content), secrets)),
      }))
      pushContext(events, message, secrets)
      continue
    }

    if (!isAssistant(message))
      continue

    const lookup = createToolCallResultLookup(message.slices, message.tool_results)
    let usedDuration = false
    for (const [index, slice] of message.slices.entries()) {
      if (slice.type === 'tool-call') {
        const outcome = toolOutcome(lookup.get(index), secrets)
        events.push(present({
          id: `${message.id ?? 'assistant'}-tool-${index}`,
          at,
          kind: 'tool',
          status: outcome.status,
          durationMs: takeDuration(durationMs, usedDuration),
          summary: outcome.summary,
          toolName: slice.toolCall.toolName,
        }))
        usedDuration = true
      }
    }

    const reply = message.slices
      .filter(slice => slice.type === 'text')
      .map(slice => slice.text)
      .join('')
      .trim()
    if (reply) {
      events.push(present({
        id: `${message.id ?? 'assistant'}-reply`,
        at,
        kind: 'reply',
        status: message.interrupted ? 'cancelled' : 'success',
        durationMs: takeDuration(durationMs, usedDuration),
        summary: clip(redact(reply, secrets)),
      }))
    }
  }

  return events
}

/**
 * Builds the text a user can copy from the workbench.
 * The first lines state the scope. Keys and bearer tokens are removed.
 *
 * @example
 * buildWorkbenchExport({ sessionId: 's1', provider: 'deepseek', model: 'deepseek-flash', phase: 'ready', events: [] })
 * // => 'Velis diagnostic summary\nScope: session id, provider, model, tool status, and redacted text. No keys.\nSession: s1\nProvider: deepseek\nModel: deepseek-flash\nVerification: ready'
 */
export function buildWorkbenchExport(input: {
  sessionId: string
  provider: string
  model: string
  phase: string
  events: WorkbenchEvent[]
  secrets?: string[]
}): string {
  const lines = [
    'Velis diagnostic summary',
    'Scope: session id, provider, model, tool status, and redacted text. No keys.',
    `Session: ${input.sessionId || 'none'}`,
    `Provider: ${input.provider || 'none'}`,
    `Model: ${input.model || 'none'}`,
    `Verification: ${input.phase}`,
    ...input.events.map((event) => {
      const time = typeof event.at === 'number' ? new Date(event.at).toISOString() : 'no-time'
      const duration = typeof event.durationMs === 'number' ? `${event.durationMs}ms` : 'no-duration'
      const tool = event.toolName ? ` ${event.toolName}` : ''
      return `${time} ${event.kind}${tool} ${event.status} ${duration} ${event.summary}`
    }),
  ]
  return redact(lines.join('\n'), input.secrets ?? [])
}

function isAssistant(message: ChatHistoryItem): message is ChatAssistantMessage & { createdAt?: number, id?: string } {
  return message.role === 'assistant' && 'slices' in message
}

function pushContext(events: WorkbenchEvent[], message: ChatHistoryItem, secrets: string[]) {
  const source = message.context?.metadata?.source
  if (!source)
    return
  events.push(present({
    id: `${message.id ?? 'message'}-context`,
    at: message.createdAt,
    kind: 'context',
    status: 'info',
    summary: clip(redact(`Context source: ${source}`, secrets)),
  }))
}

function present(event: WorkbenchEvent): WorkbenchEvent {
  return {
    id: event.id,
    kind: event.kind,
    status: event.status,
    summary: event.summary,
    ...(event.at === undefined ? {} : { at: event.at }),
    ...(event.durationMs === undefined ? {} : { durationMs: event.durationMs }),
    ...(event.toolName ? { toolName: event.toolName } : {}),
  }
}

function toolOutcome(result: ChatSlicesToolCallResult | undefined, secrets: string[]): { status: WorkbenchStatus, summary: string } {
  if (!result)
    return { status: 'running', summary: 'The tool call is still running.' }

  const rawText = redact(textFrom(result.result), secrets)
  const text = clip(rawText)
  const statusField = statusFrom(rawText)
  const reportedError = reportsError(rawText)
  if (statusField === 'requires_confirmation')
    return { status: 'pending-confirmation', summary: text }
  if ((result.isError || reportedError) && /timeout|timed out|aborted/i.test(text))
    return { status: 'timeout', summary: text }
  if ((result.isError || reportedError) && /reject|denied|refused|cancelled|canceled/i.test(text))
    return { status: 'rejected', summary: text }
  if (result.isError || reportedError)
    return { status: 'failed', summary: text }
  return { status: 'success', summary: text }
}

function statusFrom(text: string): string {
  try {
    const parsed: unknown = JSON.parse(text)
    if (typeof parsed === 'object' && parsed && 'status' in parsed && typeof parsed.status === 'string')
      return parsed.status
  }
  catch {
    return ''
  }
  return ''
}

function reportsError(text: string): boolean {
  try {
    const parsed: unknown = JSON.parse(text)
    return typeof parsed === 'object'
      && parsed !== null
      && 'isError' in parsed
      && parsed.isError === true
  }
  catch {
    return false
  }
}

function textFrom(content: unknown): string {
  if (typeof content === 'string')
    return content
  if (!Array.isArray(content))
    return ''
  return content.map((part) => {
    if (typeof part === 'object' && part && 'text' in part && typeof part.text === 'string')
      return part.text
    return ''
  }).join('')
}

function redact(text: string, secrets: string[]): string {
  return sanitizeConnectionError(text, secrets)
}

function clip(text: string): string {
  const trimmed = text.trim()
  if (trimmed.length <= summaryLimit)
    return trimmed
  return `${trimmed.slice(0, summaryLimit)}…`
}

function takeDuration(durationMs: number | undefined, used: boolean): number | undefined {
  if (used)
    return undefined
  return durationMs
}
